import FeatureFlag, { FeatureFlagFacility, FeatureFlagTenant } from "./FeatureFlag";

/**
 * Flip to true to work against generated dummy data instead of the real
 * Platform Core API (via the Command Center backend at api/FeatureFlag).
 */
const USE_DUMMY_DATA = false;

/**
 * Gates writes to Platform Core. The write wiring (FE -> Command Center ->
 * Platform Core) is complete, but writes stay disabled until the remaining
 * feature flag management work under feature 16600 is finished. While false,
 * saves and deletes are applied locally only and do not survive a reload.
 */
export const WRITE_ACTIONS_ENABLED = false;

/** Wire shape of a flag returned by api/FeatureFlag/GetAll (camelCase JSON). */
interface ConditionalFeatureFlagDetail {
  id: number;
  name: string;
  description: string | null;
  enabled: boolean;
  startDateTime: string | null;
  endDateTime: string | null;
  created: string | null;
  modified: string | null;
  tenants: ConditionalFeatureFlagTenantItem[] | null;
  facilities: ConditionalFeatureFlagFacilityDetail[] | null;
}

interface ConditionalFeatureFlagTenantItem {
  id: number;
  tenantId: number;
  tenantName: string;
  startDateTime: string | null;
  endDateTime: string | null;
}

interface ConditionalFeatureFlagFacilityDetail {
  id: number;
  tenantId: number;
  tenantName: string;
  facilityId: number;
  facilityName: string;
  startDateTime: string | null;
  endDateTime: string | null;
}

interface GetAllFeatureFlagsResponse {
  items: ConditionalFeatureFlagDetail[];
}

/**
 * Partial update for a single flag: only the fields that actually changed.
 * Matches the Platform Core PATCH contract (omitted fields are left untouched).
 */
export interface FeatureFlagPatch {
  name?: string;
  description?: string | null;
  enabled?: boolean;
  startDateTime?: string;
  endDateTime?: string;
}

/** One entry of the optimized change list managed by the page. */
export interface FeatureFlagChange {
  id: number;
  patch: FeatureFlagPatch;
}

/** Outcome of one flag's save call; errorMessage is null on success. */
export interface SaveItemResult {
  id: number;
  errorMessage: string | null;
}

/**
 * Thrown when the backend reports the saved Platform Core access token has
 * expired (401 with reason "platformCoreTokenExpired"). The page offers the
 * user a re-authentication round trip instead of showing a generic error.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super("Your Platform Core session has expired.");
    this.name = "SessionExpiredError";
  }
}

function toDate(value: string | null): Date | null {
  return value ? new Date(value) : null;
}

function toFeatureFlagTenant(item: ConditionalFeatureFlagTenantItem): FeatureFlagTenant {
  return {
    id: item.id,
    tenantId: item.tenantId,
    tenantName: item.tenantName,
    startDate: toDate(item.startDateTime),
    endDate: toDate(item.endDateTime),
  };
}

function toFeatureFlagFacility(item: ConditionalFeatureFlagFacilityDetail): FeatureFlagFacility {
  return {
    id: item.id,
    tenantId: item.tenantId,
    tenantName: item.tenantName,
    facilityId: item.facilityId,
    facilityName: item.facilityName,
    startDate: toDate(item.startDateTime),
    endDate: toDate(item.endDateTime),
  };
}

function toFeatureFlag(detail: ConditionalFeatureFlagDetail): FeatureFlag {
  return {
    id: detail.id,
    name: detail.name,
    description: detail.description ?? "",
    startDate: toDate(detail.startDateTime),
    endDate: toDate(detail.endDateTime),
    enabled: detail.enabled,
    createdDate: toDate(detail.created),
    lastModifiedDate: toDate(detail.modified),
    tenants: (detail.tenants ?? []).map(toFeatureFlagTenant),
    facilities: (detail.facilities ?? []).map(toFeatureFlagFacility),
  };
}

export async function getFeatureFlags(): Promise<FeatureFlag[]> {
  if (USE_DUMMY_DATA) {
    return GetDummyData(5, 15);
  }

  const response = await fetch("api/FeatureFlag/GetAll");
  if (!response.ok) {
    throw await toApiError(response, `Failed to load feature flags (${response.status} ${response.statusText})`);
  }

  const body: GetAllFeatureFlagsResponse = await response.json();
  return (body.items ?? []).map(toFeatureFlag);
}

/**
 * Saves the change list by sending one PATCH per changed flag, each containing
 * only the changed fields. Failures never reject the whole batch: each flag's
 * outcome is reported through onItemComplete as its call returns, and the full
 * result list is returned once every call has settled.
 */
export async function saveFeatureFlags(
  changes: FeatureFlagChange[],
  onItemComplete: (result: SaveItemResult) => void
): Promise<SaveItemResult[]> {
  if (USE_DUMMY_DATA) {
    return DummySave(changes, onItemComplete);
  }

  return Promise.all(
    changes.map(async change => {
      let errorMessage: string | null = null;
      try {
        await updateFeatureFlag(change.id, change.patch);
      } catch (e) {
        errorMessage =
          e instanceof Error && e.message ? e.message : `Failed to save feature flag ${change.id}`;
      }

      const result: SaveItemResult = { id: change.id, errorMessage };
      onItemComplete(result);
      return result;
    })
  );
}

/** Deletes a feature flag. Throws with a user-displayable message on failure. */
export async function deleteFeatureFlag(id: number): Promise<void> {
  if (USE_DUMMY_DATA) {
    return DummyDelete(id);
  }

  const response = await fetch(`api/FeatureFlag/Delete/${id}`, { method: "DELETE" });
  if (!response.ok) {
    throw await toApiError(response, `Failed to delete feature flag ${id} (${response.status})`);
  }
}

async function updateFeatureFlag(id: number, patch: FeatureFlagPatch): Promise<void> {
  const response = await fetch(`api/FeatureFlag/Update/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });

  if (!response.ok) {
    throw await toApiError(response, `Failed to save feature flag ${id} (${response.status})`);
  }
}

/**
 * Maps an error response to the error to throw. An expired-token 401 (marker
 * from RequirePlatformCoreTokenAttribute) becomes SessionExpiredError; other
 * Platform Core errors are ProblemDetails whose "detail" is user-displayable,
 * with the provided generic message as fallback for non-JSON bodies.
 */
async function toApiError(response: Response, fallback: string): Promise<Error> {
  let body: { reason?: string; detail?: string } | null = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON error body; keep the generic message.
  }

  if (response.status === 401 && body?.reason === "platformCoreTokenExpired") {
    return new SessionExpiredError();
  }

  return new Error(body?.detail ?? fallback);
}

async function DummySave(
  changes: FeatureFlagChange[],
  onItemComplete: (result: SaveItemResult) => void
): Promise<SaveItemResult[]> {
  return changes.map(change => {
    const failed = Math.random() < 0.5;
    const result: SaveItemResult = {
      id: change.id,
      errorMessage: failed ? `Simulated save failure for feature flag ${change.id}` : null,
    };
    onItemComplete(result);
    return result;
  });
}

async function DummyDelete(id: number): Promise<void> {
  if (Math.random() < 0.5) {
    throw new Error(`Simulated delete failure for feature flag ${id}`);
  }
}

async function GetDummyData(id: number, count: number = 1): Promise<FeatureFlag[]> {
  function createDummyTenants(flagId: number, tenantCount: number): FeatureFlagTenant[] {
    return Array.from({ length: tenantCount }, (_, i) => ({
      id: flagId * 100 + i,
      tenantId: 1000 + i,
      tenantName: `Tenant ${String.fromCharCode(65 + (i % 26))}${i}`,
      startDate: i % 3 === 0 ? new Date("2026-07-01T00:00:00.000-04:00") : null,
      endDate: i % 4 === 0 ? new Date("2026-12-31T00:00:00.000-05:00") : null,
    }));
  }

  function createDummyFacilities(flagId: number, facilityCount: number): FeatureFlagFacility[] {
    return Array.from({ length: facilityCount }, (_, i) => ({
      id: flagId * 100 + i,
      tenantId: 1000 + (i % 3),
      tenantName: `Tenant ${String.fromCharCode(65 + (i % 3))}`,
      facilityId: 2000 + i,
      facilityName: `Facility ${String.fromCharCode(65 + (i % 26))}${i}`,
      startDate: i % 2 === 0 ? new Date("2026-08-01T00:00:00.000-04:00") : null,
      endDate: i % 5 === 0 ? new Date("2026-11-30T00:00:00.000-05:00") : null,
    }));
  }

  function createDummyFlags(countStart: number): FeatureFlag[] {
    return [
      {
        id: id + countStart,
        name: "test1",
        description: "Short description",
        startDate: new Date("2026-07-14T00:00:00.000-04:00"),
        endDate: null,
        enabled: true,
        createdDate: new Date("2026-06-01T08:00:00.000-04:00"),
        lastModifiedDate: new Date("2026-07-10T14:30:00.000-04:00"),
        tenants: createDummyTenants(id + countStart, 7),
        facilities: createDummyFacilities(id + countStart, 12)
      },
      {
        id: id + countStart + 1,
        name: "test2",
        description: "This is a very long description. It goes on for a while. There are details, instructions, and other information. It needs to be truncated or wrapped or both else the table will break",
        startDate: new Date("2026-07-14T00:00:00.000-04:00"),
        endDate: new Date("2026-07-18T20:00:00.000-04:00"),
        enabled: false,
        createdDate: new Date("2026-05-15T09:15:00.000-04:00"),
        lastModifiedDate: null,
        tenants: createDummyTenants(id + countStart + 1, 2),
        facilities: []
      },
      {
        id: id + countStart + 2,
        name: "test3",
        description: "Medium description, clarifying",
        startDate: new Date(),
        endDate: new Date(),
        enabled: true,
        createdDate: new Date("2026-07-01T10:00:00.000-04:00"),
        lastModifiedDate: new Date(),
        tenants: [],
        facilities: createDummyFacilities(id + countStart + 2, 3)
      }
    ];
  }

  let dummyFlags: FeatureFlag[] = [];
  for (let i = 1; i < count; i++) {
    dummyFlags = [...dummyFlags, ...createDummyFlags((i - 1) * 3)];
  }

  return dummyFlags;
}
